import { scrollToTop } from '../utils/scrollUtils';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';

export default function Pagination({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  totalProducts = 0, 
  isLoading = false,
  showProductInfo = false,
  productsPerPage = 20
}) {
  const { t } = useLanguage();
  const pageNumbers = [];
  const maxVisiblePages = 5; // Adjust this to control how many page numbers are visible
  
  // Don't show pagination if there are no products or only one page
  if (totalProducts === 0 || totalPages <= 1) {
    return null;
  }

  const handlePageChange = (page) => {
    // Validate that the page number is within valid range
    if (page < 1 || page > totalPages) {
      console.error(`Invalid page number: ${page}. Total pages: ${totalPages}`);
      return;
    }
  
    onPageChange(page);
    scrollToTop();
  };

  // Determine the range of pages to display
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  // Adjust start page if at the end of the totalPages range
  if (endPage - startPage < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  // Add first page if not in range
  if (startPage > 1) {
    pageNumbers.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
      >
        1
      </button>
    );
    
    // Add ellipsis if there's a gap
    if (startPage > 2) {
      pageNumbers.push(
        <span key="ellipsis1" className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500">
          ...
        </span>
      );
    }
  }

  // Add visible page numbers
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(
      <button
        key={i}
        onClick={() => handlePageChange(i)}
        className={`inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md ${
          i === currentPage
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'text-gray-500 hover:text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
      >
        {i}
      </button>
    );
  }

  // Add last page if not in range
  if (endPage < totalPages) {
    // Add ellipsis if there's a gap
    if (endPage < totalPages - 1) {
      pageNumbers.push(
        <span key="ellipsis2" className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500">
          ...
        </span>
      );
    }
    
    pageNumbers.push(
      <button
        key={totalPages}
        onClick={() => handlePageChange(totalPages)}
        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
      >
        {totalPages}
      </button>
    );
  }

  return (
    <div className="mt-8">
      {/* Product Info Section - Only show if showProductInfo is true */}
      {showProductInfo && (
        <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
          <div>
            {!isLoading && totalProducts > 0 && (
              <span>
                {t('pagination.showingPage', { currentPage, totalPages })}
                {totalProducts > 0 && (
                  <span className="ml-2">
                    {t('pagination.total', { totalProducts: totalProducts.toLocaleString() })}
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">{t('pagination.productsPerPage', { productsPerPage })}</span>
          </div>
        </div>
      )}

      {/* Pagination Navigation */}
      <nav className="flex items-center justify-between py-4 px-4 sm:px-0 w-full">
        <div className="flex w-0 flex-1">
          <button
            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 ${
              currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <FaChevronLeft className="mr-3 h-4 w-4" />
            <span className="hidden sm:inline">{t('pagination.previous')}</span>
          </button>
        </div>

        <div className="flex items-center space-x-1">
          {pageNumbers}
        </div>

        <div className="flex w-0 flex-1 justify-end">
          <button
            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 ${
              currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <span className="hidden sm:inline">{t('pagination.next')}</span>
            <FaChevronRight className="ml-3 h-4 w-4" />
          </button>
        </div>
      </nav>
    </div>
  );
}
